import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Form } from '@/components/ui/form';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Link2, AlertCircle, Building2 } from 'lucide-react';
import { API } from '@/utils/API';
import { useSitesList } from '@/hooks/sites/useSitesList';

const connectSiteFormSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
  siteName: z.string().min(1, 'Site name is required'),
});

type ConnectSiteFormData = z.infer<typeof connectSiteFormSchema>;

interface ConnectSiteModalProps {
  isOpen: boolean;
  onClose: () => void;
  studyId: string;
  // The study's expected site type ('EAP' | 'Not EAP') — StudyBlueprint#
  // site_eap_sub_category. Used to disable unattached sites whose own EAP
  // status doesn't match, mirroring the backend check in
  // StudySite#eap_compatible_with? so the UI doesn't offer an attach the
  // server would reject.
  studySiteEapSubCategory: string;
  onSuccess?: () => void;
}

// A blank site sub_category (Ov2-imported/Hub-created sites) is treated as
// compatible with any study — mirrors StudySite#eap_compatible_with? on the
// backend.
const eapMismatchReason = (
  siteSubCategory: string,
  studySubCategory: string
): string | null => {
  if (!siteSubCategory || siteSubCategory === studySubCategory) return null;

  return `${siteSubCategory} site — this study accepts ${studySubCategory} sites`;
};

type Outcome = 'attached' | 'site_created';

const outcomeMessage = (outcome: Outcome, emailOrSiteName: string): string => {
  switch (outcome) {
    case 'attached':
      return `${emailOrSiteName} has been connected to this study.`;
    case 'site_created':
    default:
      return `A new site has been created and an invitation has been sent to ${emailOrSiteName} to manage it.`;
  }
};

// Two ways a Study Manager can connect a site to their study: attach a site
// they already manage themselves (the "Your unattached sites" list below,
// via POST /studies/:studyId/attachment_requests — StudySitePolicy already
// scopes that list to sites the current user manages), or invite someone else
// by email, which always creates a new placeholder site under this study and
// grants that person SITE_MANAGER on it (Sites::InviteOrConnectService). The
// invite path never tries to match the email against an existing orphan site
// elsewhere — if the invitee already manages a site somewhere, the invitee's
// own Site Manager must have that site connected to this study on their end
// (there is no self-service "attach my site to a study" flow yet — deferred to
// a future MR to avoid exposing every study's identity to any site manager).
const ConnectSiteModal: React.FC<ConnectSiteModalProps> = ({
  isOpen,
  onClose,
  studyId,
  studySiteEapSubCategory,
  onSuccess,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [attachingSiteId, setAttachingSiteId] = useState<string | null>(null);
  const [submissionError, setSubmissionError] = useState('');
  const [submissionSuccess, setSubmissionSuccess] = useState('');

  const { data: ownUnattachedSites = [] } = useSitesList({
    unattached: true,
    enabled: isOpen,
  });

  const form = useForm<ConnectSiteFormData>({
    resolver: zodResolver(connectSiteFormSchema),
    defaultValues: { email: '', siteName: '' },
  });

  const handleAttachOwnSite = async (siteId: string, siteName: string) => {
    setAttachingSiteId(siteId);
    setSubmissionError('');
    setSubmissionSuccess('');

    try {
      const response = await API.authenticated.fetch(
        `/studies/${studyId}/attachment_requests`,
        {
          method: 'POST',
          body: JSON.stringify({
            site_attachment_request: { study_site_id: siteId },
          }),
        }
      );

      const responseData = await response.json();

      if (!response.ok) {
        throw new Error(
          responseData.error || responseData.message || 'Failed to add site'
        );
      }

      setSubmissionSuccess(
        outcomeMessage(responseData.outcome as Outcome, siteName)
      );
      onSuccess?.();

      setTimeout(() => {
        onClose();
        setSubmissionSuccess('');
      }, 2500);
    } catch (error) {
      setSubmissionError(
        error instanceof Error ? error.message : 'An unexpected error occurred'
      );
    } finally {
      setAttachingSiteId(null);
    }
  };

  const onSubmit = async (data: ConnectSiteFormData) => {
    setIsSubmitting(true);
    setSubmissionError('');
    setSubmissionSuccess('');

    try {
      const response = await API.authenticated.fetch(
        `/studies/${studyId}/site_invitations`,
        {
          method: 'POST',
          body: JSON.stringify({
            site_invitation: {
              email: data.email,
              site_name: data.siteName,
            },
          }),
        }
      );

      const responseData = await response.json();

      if (!response.ok) {
        throw new Error(
          responseData.error || responseData.message || 'Failed to add site'
        );
      }

      setSubmissionSuccess(
        outcomeMessage(responseData.outcome as Outcome, data.email)
      );
      form.reset();
      onSuccess?.();

      setTimeout(() => {
        onClose();
        setSubmissionSuccess('');
      }, 2500);
    } catch (error) {
      setSubmissionError(
        error instanceof Error ? error.message : 'An unexpected error occurred'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting && !attachingSiteId) {
      form.reset();
      setSubmissionError('');
      setSubmissionSuccess('');
      onClose();
    }
  };

  const isBusy = isSubmitting || attachingSiteId !== null;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Link2 className="h-5 w-5" />
            Invite or Attach Site
          </DialogTitle>
          <DialogDescription>
            Attach a site you already manage, or invite someone by email to set
            up and manage a new site under this study.
          </DialogDescription>
        </DialogHeader>

        {ownUnattachedSites.length > 0 && (
          <div className="space-y-2">
            <p className="text-sm font-medium text-gray-700">
              Your unattached sites
            </p>
            <div className="space-y-2">
              {ownUnattachedSites.map(site => {
                const mismatchReason = eapMismatchReason(
                  site.submission_template?.sub_category ?? '',
                  studySiteEapSubCategory
                );

                return (
                  <div
                    key={site.id}
                    className="flex items-center justify-between gap-3 rounded-lg border border-gray-200 px-3 py-2"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <Building2 className="h-4 w-4 text-gray-400 shrink-0" />
                      <div className="min-w-0">
                        <span className="text-sm text-gray-900 truncate block">
                          {site.site_name || site.display_id || 'Unnamed site'}
                        </span>
                        {mismatchReason && (
                          <span className="text-xs text-gray-500">
                            {mismatchReason}
                          </span>
                        )}
                      </div>
                    </div>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      disabled={isBusy || !!mismatchReason}
                      onClick={() =>
                        handleAttachOwnSite(
                          site.id,
                          site.site_name || site.display_id || 'Site'
                        )
                      }
                    >
                      {attachingSiteId === site.id ? 'Attaching...' : 'Attach'}
                    </Button>
                  </div>
                );
              })}
            </div>
            <div className="relative py-2">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200" />
              </div>
              <div className="relative flex justify-center">
                <span className="bg-white px-2 text-xs text-gray-500">
                  Or invite a new site by email
                </span>
              </div>
            </div>
          </div>
        )}

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label
                  htmlFor="email"
                  className="text-sm font-medium text-gray-700"
                >
                  Email Address *
                </Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="Enter email address"
                  {...form.register('email')}
                  className="w-full"
                  disabled={isBusy}
                />
                {form.formState.errors.email && (
                  <p className="text-red-500 text-sm">
                    {form.formState.errors.email.message}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label
                  htmlFor="siteName"
                  className="text-sm font-medium text-gray-700"
                >
                  Site Name *
                </Label>
                <Input
                  id="siteName"
                  type="text"
                  placeholder="Enter site name"
                  {...form.register('siteName')}
                  className="w-full"
                  disabled={isBusy}
                />
                {form.formState.errors.siteName && (
                  <p className="text-red-500 text-sm">
                    {form.formState.errors.siteName.message}
                  </p>
                )}
                <p className="text-xs text-gray-500">
                  This always creates a new site. If this person already
                  manages a site elsewhere and you want to reuse it instead,
                  contact support to have it connected to this study.
                </p>
              </div>
            </div>

            {submissionSuccess && (
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription className="text-green-700">
                  {submissionSuccess}
                </AlertDescription>
              </Alert>
            )}

            {submissionError && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{submissionError}</AlertDescription>
              </Alert>
            )}

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={isBusy}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isBusy}
                className="bg-primary hover:bg-primary-hover text-white"
              >
                {isSubmitting ? 'Sending...' : 'Send'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default ConnectSiteModal;
